import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Zone {
  _id: string;
  name: string;
  code: string;
  status: 'Active' | 'Inactive';
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const ZoneManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch zones with server-side filtering and pagination
  const { data: zonesResponse, isLoading } = useQuery({
    queryKey: ['zone', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      // Add search
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/zone?${params.toString()}`);
      const result = response.data.data || {};
      console.log('Zone API response:', result);
      
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1
        }
      };
    },
  });

  const zones = zonesResponse?.data || [];
  const pagination = zonesResponse?.pagination;

  // Create zone mutation
  const createZoneMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/zone', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zone'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'zone'] });
      toast({
        title: 'Zone created',
        description: 'Zone has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingZone(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating zone',
          description: error.response?.data?.error?.message || error.response?.data?.error || 'Failed to create zone',
          variant: 'destructive',
        });
      }
    },
  });

  // Update zone mutation
  const updateZoneMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/zone/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zone'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'zone'] });
      toast({
        title: 'Zone updated',
        description: 'Zone has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingZone(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating zone',
          description: error.response?.data?.error?.message || error.response?.data?.error || 'Failed to update zone',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete zone mutation
  const deleteZoneMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/api/master-data/zone/${id}?soft=false`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zone'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'zone'] });
      toast({
        title: 'Zone deleted',
        description: 'Zone has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting zone',
        description: error.response?.data?.error || 'Failed to delete zone',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration with better responsive design
  const columns: TableColumn<Zone>[] = [
    {
      key: 'name',
      label: 'Zone Name',
      sortable: true,
      render: (value, zone) => (
        <div className="space-y-1">
          <div className="font-medium text-sm">
            {zone.name}
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {zone.code}
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
      render: (value, zone) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {zone.code}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, zone) => (
        <Badge 
          variant={zone.status === 'Active' ? 'default' : 'secondary'}
          className={zone.status === 'Active'
            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
            : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {zone.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, zone) => (
        <div className="text-sm text-muted-foreground">
          {new Date(zone.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      hideOnTablet: true,
      render: (value, zone) => (
        <div className="text-sm text-muted-foreground">
          {new Date(zone.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      ),
    },
  ];

  // Enhanced filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Statuses' },
        ...statusOptions
      ],
      placeholder: 'Filter by status',
    },
    // Removed date filters as they're not commonly used for zones
  ];

  // Form sections configuration with better layout
  const formSections: FormSection[] = [
    {
      title: 'Zone Information',
      description: 'Configure zone details and settings. The zone code will be auto-generated.',
      fields: [
        {
          name: 'name',
          label: 'Zone Name',
          type: 'text',
          required: true,
          placeholder: 'Enter zone name (e.g., "A", "North Zone", "East Zone")',
          description: 'A descriptive name for this zone (single character like "A" is allowed)',
          validation: z.string()
            .min(1, 'Name must be at least 1 character')
            .max(100, 'Name cannot exceed 100 characters')
            .regex(/^[a-zA-Z0-9\s\-_().]+$/, 'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' }
          ],
          description: 'Set the availability status for this zone',
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingZone(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (zone: Zone) => {
    // Transform data for form editing
    const transformedZone = {
      ...zone,
      // Ensure status is properly set for radio buttons
      status: zone.status,
    };
    setEditingZone(transformedZone);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (zone: Zone) => {
    if (window.confirm(`Are you sure you want to permanently delete "${zone.name}"? This action cannot be undone.`)) {
      deleteZoneMutation.mutate(zone._id);
    }
  };

  const handleSubmit = (data: any) => {
    // Transform form data for better API compatibility
    const transformedData = {
      ...data,
      // Convert string 'Active'/'Inactive' to proper status if needed
      status: data.status,
    };
    
    // Remove code field since it's auto-generated
    delete transformedData.code;

    if (editingZone) {
      updateZoneMutation.mutate({ id: editingZone._id, data: transformedData });
    } else {
      createZoneMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingZone(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['zone'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/zone/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Name', 'Code', 'Status', 'Created Date', 'Updated Date'];
          const csvContent = [
            headers.join(','),
            ...data.map((zone: any) => [
              `"${zone.name || ''}"`,
              `"${zone.code || ''}"`,
              `"${zone.status || ''}"`,
              `"${new Date(zone.createdAt).toLocaleDateString()}"`,
              `"${new Date(zone.updatedAt).toLocaleDateString()}"`
            ].join(','))
          ].join('\n');
          
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `zones_export_${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} zone records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No zones match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export zones',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <MasterDataTable
          title="Zone Management"
          description="Manage zones and their availability status"
          data={zones}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          searchPlaceholder="Search zones by name, code, or status..."
          filters={enhancedFilterConfig}
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleSubmit}
          sections={formSections}
          title={editingZone ? 'Edit Zone' : 'Add New Zone'}
          description={
            editingZone
              ? 'Update the zone details and availability status.'
              : 'Create a new zone for organizing geographical areas.'
          }
          isLoading={createZoneMutation.isPending || updateZoneMutation.isPending}
          initialData={editingZone || { status: 'Active', name: '', code: '' }}
          mode={editingZone ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName="Zone"
        />
      </div>
    </div>
  );
};

export default ZoneManagement;